/* eslint-disable object-shorthand */
import MouseTrap from "mousetrap";
import { Timeout, isIOS } from "../../util/Misc";
import { Constants } from "../../model/Constants";
import ModalMgr from "../../util/ModalMgr";
import { swapChildElements } from "../../util/HTMLElements";
import { emailmodalRender } from "../templates/renders/emailmodal";

/**
 * custom modal window element to show the email input box
 * @type {{template, onSendClick(): void, onKeyUp(Event): void, created(): void, tagName: string, onCancelClick(): void, validateEmail(*=): *}}
 */
const EmailModalView: any = {
    tagName: "email-modal-view",
    renderer: emailmodalRender,
    initialize(options) {
        const ciAppInstance = options.$app;
        const viewConstants = options.constants;
        const config = ciAppInstance.getUIConfig();

        viewConstants.emailText = ciAppInstance.emailModalInputLabel;
        viewConstants.cancelBtnText = ciAppInstance.emailModalCancelButtonText;
        viewConstants.sendBtnText = ciAppInstance.emailModalSendButtonText;
        viewConstants.okBtnText = ciAppInstance.emailModalOkButtonText;
        viewConstants.emailModalAutocomplete = ciAppInstance.emailModalAutocomplete;
        viewConstants.mailSentText = ciAppInstance.emailModalMailSentText;
        viewConstants.mailFailText = ciAppInstance.emailModalMailFailText;
        viewConstants.btnStyle = config.modalButtonStyles;
        viewConstants.textStyle = config.emailModelTextStyles;
        viewConstants.inputStyle = config.emailModelInputStyles;
        viewConstants.moveEmailCancelBtnToBottom = ciAppInstance.moveEmailCancelBtnToBottom;
    },
    created() {
        this.mouseTrap = new MouseTrap(this.el);

        this.emailInput = this.el.querySelector("input[type='email']");
        this.emailInput.focus();

        if (isIOS()) {
            this.emailInput.classList.add("ios");
        }

        this.swapFooterButtons();
        this.bindKeyboardShortcuts();
    },

    removed() {
        this.unBindKeyboardShortcuts();
        this.$app.getStateMgr().setState({ isModalVisible: false });
        this.$app.focusMgr.forceInputFocus();
    },

    /**
     * Bind keyboard shortcuts
     */
    bindKeyboardShortcuts() {
        this.mouseTrap.bind("escape", () => this.onCancelClick());
        this.mouseTrap.bind("enter", e => {
            if (e.target.id === 'cancel-btn') {
                this.onCancelClick();
            } else if (e.target.id === 'close-btn') {
                this.onCloseClick();
            } else {
                this.onSendClick(e);
            }
        });

        this.mouseTrap.bind("tab", (e) => ModalMgr.onTab(e, this.el));
        this.mouseTrap.bind("shift+tab", (e) => ModalMgr.onShiftTab(e, this.el));
    },

    /**
     * Unbind keyboard shortcuts
     */
    unBindKeyboardShortcuts() {
        this.mouseTrap.unbind("escape");
        this.mouseTrap.unbind("enter");
        this.mouseTrap.unbind("tab");
        this.mouseTrap.unbind("shift+tab");
    },

    onCancelClick() {
        this.el.parentElement.removeChild(this.el);
        ModalMgr.removeNegativeTabIndexOnClosingModal();
    },
    onSendClick(e) {
        // preventDefault restricts shoutui close event so that success modal is displayed
        e.preventDefault();

        const email = this.emailInput.value;

        // Send empty message to make sure any error on submit is re-read
        this.sendMessageToAriaReader("");

        if (this.validateEmail(email)) {
            const sdkInst = this.$app.getSDKInstance();
            sdkInst.requestEmailTranscript(email);
            this.$app.persistValue(Constants.EMAIL_REQ, 1);

            this.$app.shoutui.disableEmail();
            this.data.emailSent = true;
            this.sendMessageToAriaReader(this.mailSentText);
            this.el.querySelector(".js-close-button").focus();
        } else {
            this.setEmailInvalid();
            this.emailInput.focus();
        }
    },

    onCloseClick() {
        this.el.parentElement.removeChild(this.el);
        ModalMgr.removeNegativeTabIndexOnClosingModal();
    },

    /**
     * Fires on user input key up events
     * @param {Event} event - key event
     */
    onKeyUp(event) {
        const email = event.target.value;

        if (this.validateEmail(email)) {
            this.setEmailValid();
        } else if (!this.valTimeout || this.valTimeout.executed()) {
            /**
             * by default input field is valid when the there is a invalid
             * email ,invalid class is add
             * @type Timeout
             */
            this.valTimeout = Timeout.instantiate(() => {
                this.setEmailInvalid();
            }, 3000);
        } else {
            this.valTimeout.restart();
        }
    },

    /**
     * Check that the given email string is valid
     * @param {String} email - email string
     * @returns {Boolean} - true if valid
     */
    validateEmail(email) {
        const emailValidationRegEx = /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,63}(?:\.[a-z]{2})?)$/;
        return emailValidationRegEx.test(email);
    },

    /**
     * Set invalid email input state
     */
    setEmailInvalid() {
        this.data.isEmailValid = false;
        this.data.errorMsgText = this.mailFailText;

        this.sendMessageToAriaReader(this.data.errorMsgText);
    },

    setEmailValid() {
        this.data.isEmailValid = true;
        this.data.errorMsgText = "";

        if (this.valTimeout) {
            this.valTimeout.clear();
            this.valTimeout = null;
        }
    },

    /**
     * Sends a text message to the ariaReader via StateMgr
     * @param {String} textMessage - the message to send
     */
    sendMessageToAriaReader(textMessage) {
        this.$app.getStateMgr().setState({ ariaReaderSay: textMessage });
    },
    updated() {
        this.swapFooterButtons();
    },
    swapFooterButtons() {
        if (this.$app.reverseEmailModalButtons) {
            const modalFooter = this.el.querySelector(".modal-footer");
            swapChildElements(modalFooter.firstElementChild, modalFooter.lastElementChild);
        }
    },
};

/**
 * @param {CIApp} appInstance
 * @return EmailModalView
 */
export default appInstance => {
    EmailModalView.$app = appInstance;

    /**
     * @type {{}}
     */
    const constObj: any = {};

    EmailModalView.constants = constObj;

    // Dynamic data bind
    EmailModalView.data = {
        emailSent: false,
        errorMsgText: "",
        isEmailValid: true,
    };

    return EmailModalView;
};
